import axios from '@/config/axios';
import { useToast } from 'vue-toastification';

// ═══════════════════════════════════════════════════════════════════════════
// API — /admin/v1/dashboard/...
// Sửa endpoint tại đây khi backend thay đổi.
// ═══════════════════════════════════════════════════════════════════════════
function unwrap(res) {
  const { status, message, result } = res.data;
  if (status !== '200') throw new Error(message || 'Có lỗi xảy ra từ máy chủ.');
  return result;
}

export const dashboardApi = {
  /** GET /admin/v1/dashboard/stats — { totalBooks, totalUsers, activeLoans, newReviews } */
  getStats: () =>
    axios.get('admin/v1/dashboard/stats').then(unwrap),

  /** GET /admin/v1/dashboard/activityLogs — [{ id, actor, action, target, timestamp, status }] */
  getActivityLogs: () =>
    axios.get('admin/v1/dashboard/activityLogs').then(unwrap),
};
// ═══════════════════════════════════════════════════════════════════════════

const toast = {
  success(msg, options = {}) {
    return useToast().success(msg, { position: "top-center", ...options });
  },
  error(msg, options = {}) {
    return useToast().error(msg, { position: "top-center", ...options });
  },
  warning(msg, options = {}) {
    return useToast().warning(msg, { position: "top-center", ...options });
  }
};

export default {
  name: 'AdminDashboard',
  data() {
    return {
      activeTab: 'overview',
      alertVisible: true,
      searchQuery: '',
      statusFilter: 'ALL',
      sortOrder: 'asc',
      isLoading: false,
      announcementModalVisible: false,
      announcementTitle: '',
      announcementBody: '',
      tabs: [
        { id: 'overview',  label: 'Tổng quan',           shortLabel: 'Tổng quan', icon: 'fa-solid fa-chart-line'   },
        { id: 'analytics', label: 'Phân tích & Thống kê', shortLabel: 'Thống kê',  icon: 'fa-solid fa-chart-pie'    },
        { id: 'schedule',  label: 'Lịch trình bảo trì',  shortLabel: 'Bảo trì',   icon: 'fa-solid fa-calendar-days' }
      ],
      stats: { totalBooks: 0, totalUsers: 0, activeLoans: 0, newReviews: 0 },
      actionsLoading: { backup: false, clean: false },
      activityLogs: []
    };
  },
  computed: {
    sliderStyle() {
      const index = this.tabs.findIndex(t => t.id === this.activeTab);
      return {
        width:     `calc((100% - 8px) / ${this.tabs.length})`,
        transform: `translateX(calc(${index} * 100%))`
      };
    },
    filteredLogs() {
      let result = [...this.activityLogs];
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        result = result.filter(log =>
          log.actor.toLowerCase().includes(q)  ||
          log.action.toLowerCase().includes(q) ||
          log.target.toLowerCase().includes(q)
        );
      }
      if (this.statusFilter !== 'ALL') {
        const val = this.statusFilter === 'SUCCESS' ? 'Thành công' : 'Cảnh báo';
        result = result.filter(log => log.status === val);
      }
      return result.sort((a, b) => {
        const cmp = a.actor.localeCompare(b.actor, 'vi');
        return this.sortOrder === 'asc' ? cmp : -cmp;
      });
    }
  },
  mounted() {
    this.loadDashboard();
  },
  methods: {
    // ── DATA ───────────────────────────────────────────────────────────────
    loadDashboard() {
      this.isLoading = true;
      Promise.all([dashboardApi.getStats(), dashboardApi.getActivityLogs()])
        .then(([stats, logs]) => {
          this.stats        = stats;
          this.activityLogs = logs;
        })
        .catch(err => toast.error(err.message || 'Có lỗi xảy ra khi tải dữ liệu dashboard.'))
        .finally(() => { this.isLoading = false; });
    },

    // ── UI ─────────────────────────────────────────────────────────────────
    navigateTo(route) {
      this.$router.push(route).catch(err => {
        if (err.name !== 'NavigationDuplicated') console.error(err);
      });
    },
    dismissAlert() { this.alertVisible = false; },
    toggleSort()   { this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'; },

    // ── SYSTEM ACTIONS ─────────────────────────────────────────────────────
    runDatabaseBackup() {
      this.actionsLoading.backup = true;
      axios.post('admin/v1/system/backup')
        .then(() => {
          toast.success('Đã sao lưu Cơ sở dữ liệu chính thành công!');
          this._pushLog('Sao lưu DB', 'Cơ sở dữ liệu chính', 'Thành công');
        })
        .catch(err => toast.error(err.message || 'Có lỗi xảy ra khi sao lưu.'))
        .finally(() => { this.actionsLoading.backup = false; });
    },

    runSystemClean() {
      this.actionsLoading.clean = true;
      axios.post('admin/v1/system/clean')
        .then(() => {
          toast.warning('Giải phóng thành công 450MB tệp tin tạm và bộ nhớ đệm!');
          this._pushLog('Dọn dẹp rác', 'Bộ nhớ tạm cache', 'Thành công');
        })
        .catch(err => toast.error(err.message || 'Có lỗi xảy ra khi dọn dẹp.'))
        .finally(() => { this.actionsLoading.clean = false; });
    },

    // ── ANNOUNCEMENT ───────────────────────────────────────────────────────
    showAnnouncementPrompt()  { this.announcementModalVisible = true; },
    closeAnnouncementModal()  {
      this.announcementModalVisible = false;
      this.announcementTitle = '';
      this.announcementBody  = '';
    },

    sendAnnouncement() {
      axios.post('admin/v1/announcement/send', {
        title:   this.announcementTitle,
        content: this.announcementBody
      })
        .then(() => {
          toast.success(`Thông báo "${this.announcementTitle}" đã được gửi thành công!`);
          this._pushLog('Gửi thông báo', `Tiêu đề: ${this.announcementTitle}`, 'Thành công');
          this.closeAnnouncementModal();
        })
        .catch(err => toast.error(err.message || 'Có lỗi xảy ra khi gửi thông báo.'));
    },

    // ── PRIVATE ────────────────────────────────────────────────────────────
    _pushLog(action, target, status = 'Thành công') {
      this.activityLogs.unshift({
        id: Date.now(),
        actor: 'Admin Hệ thống',
        action, target,
        timestamp: 'Vừa xong',
        status
      });
    }
  }
};
